import * as assetActions from './actions';

import {
	GetAssetResponse,
	GetSensorResponse,
	getAsset,
	getAssetsPaginated,
	getSensor,
	getSystemAssetTypes,
} from '../../services/asset';
import {
	Asset,
	PaginatedAssetsResponse,
} from '../../features/AssetGrid/utils/types';
import {
	call,
	fork,
	join,
	put,
	retry,
	select,
	takeEvery,
	takeLatest,
} from 'redux-saga/effects';
import {
	selectAssetById,
	selectSensorById,
	selectSystemAssets,
} from './selectors/asset';

import { AxiosResponse } from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';
import { getAssetIdAndType } from '../../features/AssetGrid/utils/helpers';
import { getInspections } from '../inspections/actions';
import { getRiskScore } from '../risk/actions';
import { AssetSchema } from '@innovyze/lib_asset_schema';
import {
	assetSchemaSelectorByType,
	getAssetSchemaAction,
	getAssetSchemaSaga,
} from '@innovyze/lib_asset_schema_store';
import { Task } from 'redux-saga';

function* getAssetSaga(action: PayloadAction<string>) {
	// check if asset information has already been retrieved before requesting it
	const asset: ReturnType<typeof selectAssetById> = yield select(state =>
		selectAssetById(state, action.payload),
	);
	const { getAssetResolved, getAssetRejected } = assetActions;
	if (asset) return;

	const { assetType, assetId } = getAssetIdAndType(action.payload);
	try {
		const payload: AxiosResponse<GetAssetResponse> = yield retry(
			5,
			1500,
			getAsset,
			assetId,
			assetType,
		);
		if (payload.data.asset) {
			yield put(getAssetResolved(payload.data.asset));
		} else {
			yield put(getAssetRejected());
		}
	} catch (e) {
		yield put(getAssetRejected());
	}
}

function* getMultipleAssetsSaga(action: PayloadAction<string[]>) {
	const { setMultipleAssetRequestsStatus } = assetActions;
	const assetIds = action.payload;

	if (!assetIds.length) return;

	yield put(setMultipleAssetRequestsStatus('loading'));

	try {
		const tasks: Task[] = [];
		for (const assetId of assetIds) {
			tasks.push(
				yield fork(getAssetSaga, {
					type: 'asset/getAsset',
					payload: assetId,
				}),
			);
		}

		for (const task of tasks) {
			yield join(task);
		}

		yield put(setMultipleAssetRequestsStatus('success'));
	} catch (error) {
		yield put(setMultipleAssetRequestsStatus('error'));
		return;
	}
}

function* getAssetWithExtendedDataSaga(action: PayloadAction<string>) {
	const { getAsset, getAssetWithExtendedDataResolved } = assetActions;
	const { assetType } = getAssetIdAndType(action.payload);

	yield put(getAsset(action.payload));

	let assetSchema: AssetSchema | undefined = yield select(
		assetSchemaSelectorByType,
		assetType,
	);

	if (!assetSchema) {
		// Can't use put here, we have to block until this request is finished
		yield call(getAssetSchemaSaga, getAssetSchemaAction({ assetType }));
	}
	assetSchema = yield select(assetSchemaSelectorByType, assetType);

	if (assetSchema?.hasCapability('RiskScore')) {
		yield put(getRiskScore(action.payload));
	}
	if (assetSchema?.hasCapability('Inspections')) {
		yield put(getInspections(action.payload));
	}
	yield put(getAssetWithExtendedDataResolved());
}

function* getSensorSaga(action: PayloadAction<string>) {
	// check if asset information has already been retrieved before requesting it
	const sensor: ReturnType<typeof selectSensorById> = yield select(state =>
		selectSensorById(state, action.payload),
	);
	if (sensor) return;
	const { getSensorResolved, getSensorRejected } = assetActions;
	const { assetId } = getAssetIdAndType(action.payload);
	try {
		const payload: AxiosResponse<GetSensorResponse> = yield retry(
			5,
			1500,
			getSensor,
			assetId,
		);
		if (payload.data.sensor) {
			yield put(getSensorResolved(payload.data.sensor));
		} else {
			yield put(getSensorRejected());
		}
	} catch (e) {
		yield put(getSensorRejected());
	}
}

function* getSystemAssetTypesSaga() {
	const systemAssets: ReturnType<typeof selectSystemAssets> = yield select(
		selectSystemAssets,
	);
	if (Object.keys(systemAssets).length) return;
	const {
		getSystemAssetTypesResolved,
		getSystemAssetTypesRejected,
	} = assetActions;
	try {
		const payload: AxiosResponse<PaginatedAssetsResponse> = yield retry(
			5,
			1500,
			getSystemAssetTypes,
		);
		if (payload.data) {
			yield put(
				getSystemAssetTypesResolved({
					...payload.data,
				}),
			);
		} else {
			yield put(getSystemAssetTypesRejected());
		}
	} catch (e) {
		yield put(getSystemAssetTypesRejected());
	}
}

function* watchGetAsset() {
	yield takeEvery(assetActions.getAsset, getAssetSaga);
}

function* watchGetMultipleAssets() {
	yield takeLatest(assetActions.getMultipleAssets, getMultipleAssetsSaga);
}

function* watchGetAssetWithExtendedData() {
	yield takeLatest(
		assetActions.getAssetWithExtendedData,
		getAssetWithExtendedDataSaga,
	);
}

function* watchGetSensor() {
	yield takeEvery(assetActions.getSensor, getSensorSaga);
}

function* watchGetSystemAssetTypes() {
	yield takeLatest(assetActions.getSystemAssetTypes, getSystemAssetTypesSaga);
}

export default [
	watchGetAsset(),
	watchGetMultipleAssets(),
	watchGetSensor(),
	watchGetAssetWithExtendedData(),
	watchGetSystemAssetTypes(),
];
